import React, { useEffect, useRef } from "react";

// Faint node network on a canvas; nodes near the pointer light up rose and wire themselves to it.
// Without a pointer (touch, or the mouse is elsewhere) the spotlight drifts on its own.
const LINK = 110; // max distance for a link, px
const SPOT = 210; // spotlight radius, px

const NodeNetwork = ({ className = "", fadeStart = true }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const rtl = () => document.documentElement.dir === "rtl";
    let width = 0;
    let height = 0;
    let nodes = [];
    let frame = 0;
    let visible = true;
    let t = 0;
    const pointer = { x: -9999, y: -9999, active: false };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Fewer nodes on small screens to keep phones light.
      const count = Math.min(Math.round((width * height) / (width < 768 ? 6500 : 4200)), 280);
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.2 + 0.8,
      }));
    };

    const glow = (x, y) => Math.max(0, 1 - Math.hypot(x - pointer.x, y - pointer.y) / SPOT);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d > LINK) continue;
          const k = 1 - d / LINK;
          const g = Math.max(glow(a.x, a.y), glow(b.x, b.y));
          ctx.strokeStyle = g > 0 ? `rgba(251,113,133,${(0.08 + g * 0.75) * k})` : `rgba(255,255,255,${0.1 * k})`;
          ctx.lineWidth = g > 0 ? 1 : 0.7;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      const reach = SPOT * 0.9;
      ctx.lineWidth = 1;
      for (const p of nodes) {
        const d = Math.hypot(p.x - pointer.x, p.y - pointer.y);
        if (d > reach) continue;
        ctx.strokeStyle = `rgba(244,63,94,${0.7 * (1 - d / reach)})`;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(pointer.x, pointer.y);
        ctx.stroke();
      }

      for (const p of nodes) {
        const g = glow(p.x, p.y);
        ctx.fillStyle = g > 0 ? `rgba(251,113,133,${0.35 + g * 0.65})` : "rgba(255,255,255,0.32)";
        ctx.shadowColor = "#f43f5e";
        ctx.shadowBlur = g * 12;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r + g * 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = "#f43f5e";
      ctx.shadowColor = "#f43f5e";
      ctx.shadowBlur = 14;
      ctx.beginPath();
      ctx.arc(pointer.x, pointer.y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Keep the network quiet under the headline (the start side).
      if (fadeStart) {
        const w = width * 0.6;
        const fromRight = rtl();
        const fade = ctx.createLinearGradient(fromRight ? width : 0, 0, fromRight ? width - w : w, 0);
        fade.addColorStop(0, "rgba(10,10,10,0.85)");
        fade.addColorStop(1, "rgba(10,10,10,0)");
        ctx.fillStyle = fade;
        ctx.fillRect(fromRight ? width - w : 0, 0, w, height);
      }
    };

    const tick = () => {
      frame = 0;
      if (!visible) return;
      t += 0.006;
      if (!pointer.active) {
        const cx = rtl() ? 0.38 : 0.62;
        pointer.x = width * (cx + 0.22 * Math.cos(t));
        pointer.y = height * (0.5 + 0.32 * Math.sin(t * 1.4));
      }
      for (const p of nodes) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }
      draw();
      frame = requestAnimationFrame(tick);
    };

    const start = () => {
      if (!frame && visible && !reduceMotion) frame = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      pointer.active = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;
      if (pointer.active) {
        pointer.x = x;
        pointer.y = y;
      }
      if (reduceMotion) draw();
    };

    resize();
    if (reduceMotion) {
      pointer.x = width * 0.65;
      pointer.y = height * 0.45;
      draw();
    }

    // Pause when the hero scrolls out of view or the tab is hidden.
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && !document.hidden;
      if (visible) start();
    });
    observer.observe(canvas);
    const onVisibility = () => {
      visible = !document.hidden;
      if (visible) start();
    };
    const onResize = () => {
      resize();
      draw();
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    start();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fadeStart]);

  return <canvas ref={canvasRef} className={`pointer-events-none ${className}`} aria-hidden="true" />;
};

export default NodeNetwork;
